/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import l18n from '../../shared/strings';
import rollbackCreatedPreset from './rollbackCreatedPreset';
import { getPresets } from './getPresets';

const IMAGE_FIELDS = ['logoUrl', 'avatarUrl', 'backgroundLoginPageUrl', 'thumbnailUrl'];

export const createPreset = createAsyncThunk(
  'websiteSettings/createPreset',
  async ({
    name, settings, imageData, isPublished, updateWebsiteSettings,
  }, {
    dispatch,
    extra: {
      UiBlocker, sdk, utils, Logger, showErrorDialog,
    },
  }) => {
    let createdPresetId = null;

    try {
      UiBlocker.block('Saving preset...');

      const settingsForCreate = { ...settings };
      IMAGE_FIELDS.forEach((field) => {
        if (settingsForCreate[field]) settingsForCreate[field] = '';
      });

      const { data } = await sdk.websiteSettingsPresets.create(name, settingsForCreate);
      createdPresetId = data._id;

      if (isPublished) {
        if (settings?.avatarUrl) {
          const { data: { url } } = await sdk.websiteSettingsPresets.uploadAvatar(data._id, settings?.avatarUrl);
          data.settings.avatarUrl = url;
        }
        if (settings?.backgroundLoginPageUrl) {
          const { data: { url } } = await sdk.websiteSettingsPresets.uploadBackground(data._id, settings?.backgroundLoginPageUrl);
          data.settings.backgroundLoginPageUrl = url;
        }
        if (settings?.thumbnailUrl) {
          const { data: { url } } = await sdk.websiteSettingsPresets.uploadThumbnail(data._id, settings?.thumbnailUrl);
          data.settings.thumbnailUrl = url;
        }
        if (settings?.logoUrl) {
          const { data: { url } } = await sdk.websiteSettingsPresets.uploadLogo(data._id, settings?.logoUrl);
          data.settings.logoUrl = url;
        }
      } else if (imageData) {
        await Promise.all(Object.keys(imageData).map(async (imageName) => {
          const { file } = imageData[imageName];
          if (imageName === 'avatarUrl') {
            const { data: { url } } = await sdk.websiteSettingsPresets.uploadAvatar(data._id, file).promise;
            data.settings.avatarUrl = url;
          }
          if (imageName === 'backgroundLoginPageUrl') {
            const { data: { url } } = await sdk.websiteSettingsPresets.uploadBackground(data._id, file).promise;
            data.settings.backgroundLoginPageUrl = url;
          }
          if (imageName === 'thumbnailUrl') {
            const { data: { url } } = await sdk.websiteSettingsPresets.uploadThumbnail(data._id, file).promise;
            data.settings.thumbnailUrl = url;
          }
          if (imageName === 'logoUrl') {
            const { data: { url } } = await sdk.websiteSettingsPresets.uploadLogo(data._id, file).promise;
            data.settings.logoUrl = url;
          }
        }));
        updateWebsiteSettings(data.settings);
      }

      Logger.log('User', 'WebsitePresetCreated', { preset_name: data.name, preset_id: data._id });

      return data;
    } catch (error) {
      let rollbackFailed = false;

      if (createdPresetId) {
        const rollbackSucceeded = await rollbackCreatedPreset({
          presetId: createdPresetId,
          sdk,
          Logger,
        });

        if (!rollbackSucceeded) {
          rollbackFailed = true;

          try {
            await dispatch(getPresets());
          } catch (refreshError) {
            Logger.error(new Error('Error refreshing website presets after rollback failure'), { error: refreshError }, [
              'RefreshWebsitePresetsAfterRollbackFailed',
              { errorMessage: (refreshError && refreshError.message) || 'NoMessage', presetId: createdPresetId },
            ]);
          }
        }
      }

      Logger.log('User', 'WebsitePresetNotCreated');

      const uploadErrorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not be create preset';
      const errorMessage = rollbackFailed
        ? `${uploadErrorMessage}. ${l18n.WEBSITES.textPresetRollbackFailed}`
        : uploadErrorMessage;

      showErrorDialog(errorMessage);
      Logger.error(new Error('Error preset creating'), { error }, [
        'CreateWebsitePresetFailed',
        {
          errorMessage: (error && error.message) || 'NoMessage',
          rollbackFailed,
          presetId: createdPresetId || '',
        },
      ]);
      throw error;
    } finally {
      UiBlocker.unblock();
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(
      createPreset.fulfilled,
      (state, { payload }) => {
        state.presets.unshift(payload);
        state.error = null;
      },
    )
    .addCase(
      createPreset.rejected,
      (state, { error }) => {
        state.error = error;
      },
    );
};
